import React, { useCallback } from 'react';
import Masonry from 'react-smart-masonry';
import { OrderShefFragment, useLikeShefPostMutation, useShefPostsQuery } from 'src/gqlReactTypings.generated.d';
import { Page } from 'src/shared/design-system/ShefDashboard';
import { Post } from 'src/shared/design-system/ShefSocial';
import styled from 'styled-components';
import { Border, Colors, Font, Mixins, ScreenWidth, ScreenWidthValues, Spacing } from 'web-common/src/shared/styles';

const Container = styled.div`
  flex: 1 1 100%;
  width: 100%;

  ${Mixins.Media.maxWidthSmall(`
    margin-bottom: ${Spacing.SINGLE_HALF};
  `)}
`;

const NoStoriesContainer = styled.div`
  background: ${Colors.GRAY_LITE_20};
  border-radius: ${Border.Radius.M};
  margin: ${Spacing.HALF} ${Spacing.SINGLE} ${Spacing.SINGLE};
  padding: ${Spacing.HALF} ${Spacing.THREE_QUARTERS};
  ${Font.bySize(Font.Size.XS)}

  ${Mixins.Media.maxWidthSmall(`
    margin: ${Spacing.HALF} auto ${Spacing.DOUBLE};
  `)}
`;

const StoryGrid = styled(Masonry).attrs(() => ({
  autoArrange: true,
  breakpoints: {
    mobileSmall: ScreenWidthValues[ScreenWidth.EXTRA_EXTRA_SMALL],
    mobile: ScreenWidthValues[ScreenWidth.EXTRA_SMALL],
    tablet: ScreenWidthValues[ScreenWidth.MEDIUM],
    desktop: ScreenWidthValues[ScreenWidth.LARGE],
  },
  columns: {
    mobileSmall: 1,
    mobile: 2,
    tablet: 2,
    desktop: 3,
  },
  gap: Spacing.SINGLE_HALF,
}))`
  padding: 0 ${Spacing.SINGLE};
`;

interface ConsumerOrderShefStoriesProps {
  shef: OrderShefFragment;
}

export const ConsumerOrderShefStories: React.FC<ConsumerOrderShefStoriesProps> = (props) => {
  const { shef } = props;
  const { id, publicName } = shef;
  const { data, loading } = useShefPostsQuery({ variables: { shefId: id } });
  const [likeShefPost] = useLikeShefPostMutation();
  const hasPosts = !!data?.shefPosts.length;

  const onClickLike = useCallback(
    (postId: string) => {
      likeShefPost({ variables: { id: postId } });
    },
    [likeShefPost]
  );

  if (loading || !data) {
    return null;
  }

  return !hasPosts ? (
    <NoStoriesContainer>{publicName} has not posted any stories yet.</NoStoriesContainer>
  ) : (
    <Container>
      <StoryGrid>
        {data.shefPosts.map((post, index) => (
          <Page.Panel key={`${post.id}-${index}`} noContentPadding>
            <Post
              {...post}
              isOnMenuPage
              shefName={shef.publicName}
              shefImageUrl={shef.imageUrl ?? undefined}
              showLikes
              onClickLike={onClickLike}
            />
          </Page.Panel>
        ))}
      </StoryGrid>
    </Container>
  );
};
